import { Directive, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { DateFns } from "@/shared/date-fns";
import { KpiDates, KpiRangeFns } from "../kpi-range-fns";

/** The part of a KPI that is a share of a whole: amt out of totalAmt. */
export interface KpiResult {
  amt: number;
  totalAmt: number;
}

/** Shared by every KPI widget: the KPI Range input, reload on change, the stale-load guard and
 *  rangeText. A subclass says how to fetch its result and where to put it. KPIs cover every account
 *  except those excluded from analytics - the dashboard's Account Filter does not apply to them. */
@Directive()
export abstract class KpiRangeWidgetBase<T> implements OnInit, OnChanges {
  /** Null = the KPI Range is an incomplete Date Range. */
  @Input() kpiDates: KpiDates | null = null;
  /** Accounts set to Exclude from Dashboard Analytics that this view puts back in. */
  @Input() includedAccountIds: number[] = [];

  isReady = false;
  rangeText = "";

  private loadToken = 0;

  /** fromDate / toDate are inclusive yyyy-MM-dd. */
  protected abstract fetch(fromDate: string, toDate: string): Promise<T>;

  protected abstract apply(result: T): void;

  async ngOnInit() {
    await this.load();
  }

  async ngOnChanges(changes: SimpleChanges) {
    const changed = changes["kpiDates"] ?? changes["includedAccountIds"];
    if (changed && !changed.firstChange) await this.load();
  }

  /** A newer load (the range changed again) wins over a slower older one. */
  private async load() {
    const token = ++this.loadToken;
    if (!this.kpiDates) {
      this.isReady = true;
      return;
    }
    this.isReady = false;
    const r = await this.fetch(KpiRangeFns.toIsoDay(this.kpiDates.from), KpiRangeFns.toIsoDay(this.kpiDates.to));
    if (token !== this.loadToken) return;

    this.apply(r);
    this.rangeText = `${DateFns.fmtDate(this.kpiDates.from)} - ${DateFns.fmtDate(this.kpiDates.to)}`;
    this.isReady = true;
  }
}

/** A KPI that is one number and its share of a total. A subclass only says how to fetch the two numbers. */
@Directive()
export abstract class KpiWidgetBase extends KpiRangeWidgetBase<KpiResult> {
  amt = 0;
  totalAmt = 0;
  /** Null when the total is zero - a percentage of nothing means nothing. */
  pct: number | null = null;

  protected apply(r: KpiResult) {
    this.amt = r.amt;
    this.totalAmt = r.totalAmt;
    this.pct = r.totalAmt === 0 ? null : (r.amt / r.totalAmt) * 100;
  }
}
